import { Dialog } from "@headlessui/react";
import React, { useState } from "react";
import Input from "../../../components/controls/Input";
import Button from "../../../components/ui/Button";
import { lastNine } from "../employeeProductionHelpers";
import {
  ReceivingItem,
  SaveScannedItemPayload,
} from "../employeeProductionTypes";

type Props = {
  item: ReceivingItem;
  productionId: number;
  suggestedAmount: number;
  onConfirm: (payload: SaveScannedItemPayload) => Promise<void>;
  onClose: () => void;
};

export function ConfirmScannedAmountDialog({
  item,
  productionId,
  suggestedAmount,
  onConfirm,
  onClose,
}: Props) {
  const [amount, setAmount] = useState(String(suggestedAmount));
  const [useFullAmount, setUseFullAmount] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    const finalAmount = useFullAmount ? item.received_amount : Number(amount);
    if (!Number.isFinite(finalAmount) || finalAmount <= 0) {
      setError("Amount must be greater than 0");
      return;
    }
    if (finalAmount > item.received_amount) {
      setError(
        `Amount cannot exceed ${item.received_amount.toFixed(2)} ${item.unit_of_measure ?? ""}`,
      );
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm({
        production_id: productionId,
        barcode: item.barcode,
        used_amount: finalAmount,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mt-3 text-center sm:mt-5">
        <Dialog.Title className="text-medium font-bold leading-6 text-gray-900">
          {item.product_name || "N/A"}
        </Dialog.Title>
        <p className="text-base font-semibold leading-6 text-gray-900">
          LOT: {item.lot_number || "N/A"}
        </p>
        <p className="text-base font-semibold leading-6 text-gray-900">
          Barcode: {lastNine(item.barcode)}
        </p>

        <div className="mt-4 text-left">
          <p className="mb-4 text-sm font-semibold text-gray-600">
            Available amount: {item.received_amount}{" "}
            {item.unit_of_measure ?? ""}
          </p>
          <label className="mb-4 flex items-center">
            <input
              type="checkbox"
              checked={useFullAmount}
              onChange={(event) => {
                setUseFullAmount(event.target.checked);
                setError("");
              }}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm font-semibold text-gray-700">
              Use full amount
            </span>
          </label>

          {!useFullAmount && (
            <Input
              label="Amount"
              name="amount"
              type="number"
              step="0.01"
              min="0"
              max={item.received_amount}
              value={amount}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setAmount(event.target.value);
                setError("");
              }}
              labelOn={true}
              suffix={item.unit_of_measure ?? ""}
            />
          )}
          {error && (
            <p className="mt-1 text-sm font-semibold text-red-500">{error}</p>
          )}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-6">
        <Button
          variant="secondary"
          fullWidth
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button fullWidth onClick={submit} loading={submitting}>
          Add
        </Button>
      </div>
    </div>
  );
}
